import { supabase } from '../lib/supabase';
import { compressImage } from '../utils/imageCompression';
import { getCurrentOrgId } from '../lib/getCurrentOrgId';
import { isAdmin } from '../utils/permissions';

const BUCKET = 'daily-log-photos';

const sanitizeFilename = (name) => {
  return name
    ?.normalize('NFD')
    ?.replace(/[\u0300-\u036f]/g, '')
    ?.toLowerCase()
    ?.replace(/\s+/g, '_')
    ?.replace(/[^a-z0-9_.]/g, '');
};

/**
 * Permission helpers
 */
export const canEdit = (project, userId) => {
  if (!project || !userId) return false;
  // projectService.getById() returns camelCase via toCamelCase(); support both
  const pmId = project?.pmUserId ?? project?.pm_user_id;
  // After MIG 2: column renamed to supervisor_user_id; support both names for safety
  const superId = project?.supervisorUserId ?? project?.supervisor_user_id
    ?? project?.superintendentUserId ?? project?.superintendent_user_id;
  return pmId === userId || superId === userId;
};

export const canView = (project, userId) => {
  if (!project || !userId) return false;
  // projectService.getById() returns camelCase via toCamelCase(); support both
  const pmId = project?.pmUserId ?? project?.pm_user_id;
  // After MIG 2: column renamed to supervisor_user_id; support both names for safety
  const superId = project?.supervisorUserId ?? project?.supervisor_user_id
    ?? project?.superintendentUserId ?? project?.superintendent_user_id;
  const coordId = project?.coordinatorUserId ?? project?.coordinator_user_id;
  return pmId === userId || superId === userId || coordId === userId;
};

/**
 * canApprove: PM of the project or admin only.
 *
 * Volontairement DIFFERENT de canEdit : le surintendant garde la creation,
 * l'edition et la soumission (canEdit), mais n'approuve plus. L'admin, lui,
 * approuve sans etre membre du projet.
 *
 * isPM() n'est PAS utilise : il teste le ROLE, pas l'appartenance au projet —
 * un PM d'un autre chantier y repondrait vrai.
 */
export const canApprove = (project, user, userProfile) => {
  if (isAdmin(userProfile)) return true;
  if (!project || !user?.id) return false;
  // projectService.getById() returns camelCase via toCamelCase(); support both
  const pmId = project?.pmUserId ?? project?.pm_user_id;
  return pmId === user?.id;
};

/**
 * Generate signed URLs for an array of storage paths.
 * Returns a map of path → signedUrl.
 */
const getSignedUrls = async (paths) => {
  if (!paths || paths?.length === 0) return {};
  try {
    const { data, error } = await supabase?.storage
      ?.from(BUCKET)
      ?.createSignedUrls(paths, 3600);
    if (error) throw error;
    const map = {};
    (data || [])?.forEach((item) => {
      if (item?.path) map[item?.path] = item?.signedUrl;
    });
    return map;
  } catch (err) {
    console.error('getSignedUrls error:', err);
    return {};
  }
};

/**
 * Enrich a log row with signed photo URLs.
 */
const enrichPhotos = async (photos) => {
  if (!photos || photos?.length === 0) return [];
  const paths = photos?.map((p) => p?.storage_path);
  const urlMap = await getSignedUrls(paths);
  return photos?.map((p) => ({ ...p, signedUrl: urlMap?.[p?.storage_path] || null }));
};

/**
 * Fetch all logs for a project, ordered by date DESC.
 * Includes crew (with subcontractor names + nested tasks), photos (with signed URLs),
 * and approval info (approval_status, approved_by, approved_at + approver name).
 */
export const getByProject = async (projectId) => {
  try {
    const { data: logs, error } = await supabase
      ?.from('daily_logs')
      ?.select(`
        *,
        creator:created_by(id, full_name, email),
        updater:updated_by(id, full_name, email),
        approver:approved_by(id, full_name, email),
        crew:daily_log_crew(
          id,
          daily_log_id,
          subcontractor_id,
          worker_count,
          notes,
          created_at,
          subcontractor:subcontractors(id, company_name, trade),
          tasks:daily_log_crew_tasks(id, nom, pourcentage, sort_order, previous_task_id, completed, completed_at)
        ),
        daily_log_photos(id, storage_path, caption, uploaded_by, uploaded_at)
      `)
      ?.eq('project_id', projectId)
      ?.order('log_date', { ascending: false });

    if (error) throw error;

    const enriched = await Promise.all(
      (logs || [])?.map(async (log) => {
        const photos = await enrichPhotos(log?.daily_log_photos || []);
        // Sort tasks by sort_order client-side
        const crew = (log?.crew || [])?.map((c) => ({
          ...c,
          tasks: (c?.tasks || [])?.sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0)),
        }));
        return {
          ...log,
          daily_log_photos: photos,
          crew,
        };
      })
    );

    return { data: enriched, error: null };
  } catch (error) {
    console.error('getByProject error:', error);
    return { data: null, error };
  }
};

/**
 * Fetch a single log for a specific date with crew + nested tasks + photos.
 */
export const getByProjectAndDate = async (projectId, date) => {
  try {
    const { data: log, error } = await supabase
      ?.from('daily_logs')
      ?.select(`
        *,
        creator:created_by(id, full_name, email),
        updater:updated_by(id, full_name, email),
        crew:daily_log_crew(
          id,
          daily_log_id,
          subcontractor_id,
          worker_count,
          notes,
          created_at,
          subcontractor:subcontractors(id, company_name, trade),
          tasks:daily_log_crew_tasks(id, nom, pourcentage, sort_order, previous_task_id, completed, completed_at)
        ),
        daily_log_photos(id, storage_path, caption, uploaded_by, uploaded_at)
      `)
      ?.eq('project_id', projectId)
      ?.eq('log_date', date)
      ?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    if (!log) return { data: null, error: null };

    const photos = await enrichPhotos(log?.daily_log_photos || []);
    const crew = (log?.crew || [])?.map((c) => ({
      ...c,
      tasks: (c?.tasks || [])?.sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0)),
    }));
    return {
      data: {
        ...log,
        daily_log_photos: photos,
        crew,
      },
      error: null,
    };
  } catch (error) {
    console.error('getByProjectAndDate error:', error);
    return { data: null, error };
  }
};

/**
 * Fetch a single daily log by its UUID.
 * Includes crew (with subcontractor names + trade + nested tasks), photos (with signed URLs),
 * and approval info.
 */
export const getById = async (id) => {
  try {
    const { data: log, error } = await supabase
      ?.from('daily_logs')
      ?.select(`
        *,
        creator:created_by(id, full_name, email),
        updater:updated_by(id, full_name, email),
        approver:approved_by(id, full_name, email),
        crew:daily_log_crew(
          id,
          daily_log_id,
          subcontractor_id,
          worker_count,
          notes,
          created_at,
          subcontractor:subcontractors(id, company_name, trade),
          tasks:daily_log_crew_tasks(id, nom, pourcentage, sort_order)
        ),
        daily_log_photos(id, storage_path, caption, uploaded_by, uploaded_at)
      `)
      ?.eq('id', id)
      ?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    if (!log) return { data: null, error: null };

    const photos = await enrichPhotos(log?.daily_log_photos || []);
    const crew = (log?.crew || [])?.map((c) => ({
      ...c,
      tasks: (c?.tasks || [])?.sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0)),
    }));
    return {
      data: {
        ...log,
        daily_log_photos: photos,
        crew,
      },
      error: null,
    };
  } catch (error) {
    console.error('getById error:', error);
    return { data: null, error };
  }
};

/**
 * For the badge feature: returns list of project IDs where user is PM/Super
 * AND no log exists for yesterday or today.
 */
export const getMissingLogProjects = async (userId) => {
  try {
    // Get projects where user is PM or Supervisor (renamed from superintendent)
    const { data: projects, error: projError } = await supabase
      ?.from('projects')
      ?.select('id, pm_user_id, supervisor_user_id')
      ?.or(`pm_user_id.eq.${userId},supervisor_user_id.eq.${userId}`);

    if (projError) throw projError;
    if (!projects || projects?.length === 0) return { data: [], error: null };

    const today = new Date();
    const todayStr = today?.toISOString()?.split('T')?.[0];
    const yesterday = new Date(today);
    yesterday?.setDate(yesterday?.getDate() - 1);
    const yesterdayStr = yesterday?.toISOString()?.split('T')?.[0];

    const projectIds = projects?.map((p) => p?.id);

    // Fetch logs for today and yesterday for these projects
    const { data: logs, error: logsError } = await supabase
      ?.from('daily_logs')
      ?.select('project_id, log_date')
      ?.in('project_id', projectIds)
      ?.in('log_date', [todayStr, yesterdayStr]);

    if (logsError) throw logsError;

    // Build a set of "project_id|date" that have logs
    const logSet = new Set((logs || [])?.map((l) => `${l?.project_id}|${l?.log_date}`));

    // For each project, determine missing days
    const result = projectIds?.reduce((acc, pid) => {
      const hasToday = logSet?.has(`${pid}|${todayStr}`);
      const hasYesterday = logSet?.has(`${pid}|${yesterdayStr}`);

      let missingCount = 0;
      if (!hasToday) missingCount++;
      if (!hasYesterday) missingCount++;

      if (missingCount > 0) {
        acc?.push({ projectId: pid, missingCount });
      }
      return acc;
    }, []);

    return { data: result, error: null };
  } catch (error) {
    console.error('getMissingLogProjects error:', error);
    return { data: [], error };
  }
};

/**
 * Create a new daily log with crew rows and photo uploads.
 * payload: { projectId, organizationId, logDate, weatherCondition, weatherTemperature,
 *            workPerformed, notes, crew: [{subcontractorId, workerCount, notes}],
 *            photos: [{file, caption}], userId }
 */
export const create = async (payload) => {
  try {
    const {
      projectId,
      organizationId,
      logDate,
      weatherCondition,
      weatherTemperature,
      workPerformed,
      notes,
      materialsDelivered,
      issueType,
      issueDescription,
      crew = [],
      photos = [],
      userId,
    } = payload;

    // Insert the log
    const { data: log, error: logError } = await supabase
      ?.from('daily_logs')
      ?.insert({
        project_id: projectId,
        organization_id: organizationId,
        log_date: logDate,
        weather_condition: weatherCondition || null,
        weather_temperature: weatherTemperature || null,
        work_performed: workPerformed || null,
        notes: notes || null,
        materials_delivered: materialsDelivered || null,
        issue_type: issueType || null,
        issue_description: issueDescription || null,
        created_by: userId || null,
        updated_by: userId || null,
      })
      ?.select()
      ?.single();

    if (logError) throw logError;

    // Insert crew rows
    if (crew?.length > 0) {
      const crewRows = crew?.map((c) => ({
        daily_log_id: log?.id,
        subcontractor_id: c?.subcontractorId,
        worker_count: c?.workerCount || null,
        notes: c?.notes || null,
      }));
      const { error: crewError } = await supabase?.from('daily_log_crew')?.insert(crewRows);
      if (crewError) throw crewError;
    }

    // Upload photos
    if (photos?.length > 0) {
      await uploadPhotos(projectId, log?.id, photos, userId);
    }

    return { data: log, error: null };
  } catch (error) {
    console.error('create daily log error:', error);
    return { data: null, error };
  }
};

/**
 * Update an existing daily log.
 * payload: { weatherCondition, weatherTemperature, workPerformed, notes,
 *            crew: [{subcontractorId, workerCount, notes}],
 *            newPhotos: [{file, caption}], userId,
 *            skipCrewDelete: boolean — if true, skip crew delete/re-insert (caller handles crew diff) }
 */
export const update = async (id, payload) => {
  try {
    const {
      weatherCondition,
      weatherTemperature,
      workPerformed,
      notes,
      materialsDelivered,
      issueType,
      issueDescription,
      crew = [],
      newPhotos = [],
      userId,
      projectId,
      skipCrewDelete = false,
      logDate,
    } = payload;

    // Update log fields
    const { data: log, error: logError } = await supabase
      ?.from('daily_logs')
      ?.update({
        weather_condition: weatherCondition || null,
        weather_temperature: weatherTemperature || null,
        work_performed: workPerformed || null,
        notes: notes || null,
        materials_delivered: materialsDelivered || null,
        issue_type: issueType || null,
        issue_description: issueDescription || null,
        updated_by: userId || null,
        // La date n'est ecrite que si l'appelant l'envoie (elle a change).
        ...(logDate ? { log_date: logDate } : {}),
      })
      ?.eq('id', id)
      ?.select()
      ?.single();

    if (logError) throw logError;

    if (!skipCrewDelete) {
      // Replace crew rows: delete existing, re-insert
      await supabase?.from('daily_log_crew')?.delete()?.eq('daily_log_id', id);

      if (crew?.length > 0) {
        const crewRows = crew?.map((c) => ({
          daily_log_id: id,
          subcontractor_id: c?.subcontractorId,
          worker_count: c?.workerCount || null,
          notes: c?.notes || null,
        }));
        const { error: crewError } = await supabase?.from('daily_log_crew')?.insert(crewRows);
        if (crewError) throw crewError;
      }
    }

    // Upload new photos (existing photos are kept)
    if (newPhotos?.length > 0 && projectId) {
      await uploadPhotos(projectId, id, newPhotos, userId);
    }

    return { data: log, error: null };
  } catch (error) {
    console.error('update daily log error:', error);
    return { data: null, error };
  }
};

/**
 * Approve a daily log.
 * Sets approval_status = 'approved', approved_by = userId, approved_at = now()
 */
export const approve = async (logId, userId) => {
  try {
    const { data, error } = await supabase
      ?.from('daily_logs')
      ?.update({
        approval_status: 'approved',
        approved_by: userId,
        approved_at: new Date()?.toISOString(),
      })
      ?.eq('id', logId)
      ?.select(`
        *,
        creator:created_by(id, full_name, email),
        updater:updated_by(id, full_name, email),
        approver:approved_by(id, full_name, email),
        crew:daily_log_crew(
          id,
          daily_log_id,
          subcontractor_id,
          worker_count,
          notes,
          created_at,
          subcontractor:subcontractors(id, company_name, trade),
          tasks:daily_log_crew_tasks(id, nom, pourcentage, sort_order)
        ),
        daily_log_photos(id, storage_path, caption, uploaded_by, uploaded_at)
      `)
      ?.maybeSingle();

    if (error) throw error;
    if (data) {
      const photos = await enrichPhotos(data?.daily_log_photos || []);
      const crew = (data?.crew || [])?.map((c) => ({
        ...c,
        tasks: (c?.tasks || [])?.sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0)),
      }));
      return {
        data: {
          ...data,
          daily_log_photos: photos,
          crew,
        },
        error: null,
      };
    }
    return { data, error: null };
  } catch (error) {
    console.error('approve error:', error);
    return { data: null, error };
  }
};

/**
 * Unapprove a daily log (revert to submitted).
 * Sets approval_status = 'submitted', approved_by = null, approved_at = null
 */
export const unapprove = async (logId) => {
  try {
    const { data, error } = await supabase
      ?.from('daily_logs')
      ?.update({
        approval_status: 'submitted',
        approved_by: null,
        approved_at: null,
      })
      ?.eq('id', logId)
      ?.select(`
        *,
        creator:created_by(id, full_name, email),
        updater:updated_by(id, full_name, email),
        approver:approved_by(id, full_name, email),
        crew:daily_log_crew(
          id,
          daily_log_id,
          subcontractor_id,
          worker_count,
          notes,
          created_at,
          subcontractor:subcontractors(id, company_name, trade),
          tasks:daily_log_crew_tasks(id, nom, pourcentage, sort_order)
        ),
        daily_log_photos(id, storage_path, caption, uploaded_by, uploaded_at)
      `)
      ?.maybeSingle();

    if (error) throw error;
    if (data) {
      const photos = await enrichPhotos(data?.daily_log_photos || []);
      const crew = (data?.crew || [])?.map((c) => ({
        ...c,
        tasks: (c?.tasks || [])?.sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0)),
      }));
      return {
        data: {
          ...data,
          daily_log_photos: photos,
          crew,
        },
        error: null,
      };
    }
    return { data, error: null };
  } catch (error) {
    console.error('unapprove error:', error);
    return { data: null, error };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Task CRUD methods (daily_log_crew_tasks)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Add a task to a crew row.
 * @param {string} crewId - UUID of the daily_log_crew row
 * @param {{ nom: string, pourcentage?: number, sort_order?: number }} taskData
 */
export const addTask = async (crewId, { nom, pourcentage = 0, sort_order = 0, previous_task_id = null, organization_id }) => {
  try {
    const orgId = organization_id || (await getCurrentOrgId());
    if (!orgId) throw new Error('Could not resolve organization_id');

    const isCompleted = pourcentage >= 100;
    const payload = {
      crew_id: crewId,
      nom,
      pourcentage,
      sort_order,
      organization_id: orgId,
      previous_task_id: previous_task_id || null,
      completed: isCompleted,
      completed_at: isCompleted ? new Date()?.toISOString() : null,
    };

    const { data, error } = await supabase
      ?.from('daily_log_crew_tasks')
      ?.insert(payload)
      ?.select()
      ?.single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('addTask error:', error);
    return { data: null, error };
  }
};

/**
 * Update an existing task.
 * @param {string} taskId - UUID of the daily_log_crew_tasks row
 * @param {{ nom?: string, pourcentage?: number, sort_order?: number }} patch
 */
export const updateTask = async (taskId, { nom, pourcentage, sort_order }) => {
  try {
    const patch = {};
    if (nom !== undefined) patch.nom = nom;
    if (pourcentage !== undefined) {
      patch.pourcentage = pourcentage;
      patch.completed = pourcentage >= 100;
      patch.completed_at = pourcentage >= 100 ? new Date()?.toISOString() : null;
    }
    if (sort_order !== undefined) patch.sort_order = sort_order;
    patch.updated_at = new Date()?.toISOString();

    const { data, error } = await supabase
      ?.from('daily_log_crew_tasks')
      ?.update(patch)
      ?.eq('id', taskId)
      ?.select()
      ?.single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('updateTask error:', error);
    return { data: null, error };
  }
};

/**
 * Delete a task by ID.
 * @param {string} taskId - UUID of the daily_log_crew_tasks row
 */
export const deleteTask = async (taskId) => {
  try {
    const { error } = await supabase
      ?.from('daily_log_crew_tasks')
      ?.delete()
      ?.eq('id', taskId);

    if (error) throw error;
    return { data: { success: true }, error: null };
  } catch (error) {
    console.error('deleteTask error:', error);
    return { data: null, error };
  }
};

/**
 * List all tasks for a crew row, ordered by sort_order ASC.
 * @param {string} crewId - UUID of the daily_log_crew row
 */
export const listTasksByCrew = async (crewId) => {
  try {
    const { data, error } = await supabase
      ?.from('daily_log_crew_tasks')
      ?.select('*')
      ?.eq('crew_id', crewId)
      ?.order('sort_order', { ascending: true });

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('listTasksByCrew error:', error);
    return { data: [], error };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Photo helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Upload photos to storage and insert rows in daily_log_photos.
 */
const uploadPhotos = async (projectId, logId, photos, userId) => {
  const photoRows = [];
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  const MAX_BYTES = 20 * 1024 * 1024; // 20 MB

  // Compress all photos in parallel
  const compressedItems = await Promise.all(
    photos?.map(async (photoItem) => {
      const file = photoItem?.file;
      if (!file) return null;

      // Validate type
      if (!ALLOWED_TYPES?.includes(file?.type?.toLowerCase())) return null;

      // Validate size before compression
      if (file?.size > MAX_BYTES) {
        console.warn(`[uploadPhotos] File too large (${(file?.size / 1024 / 1024)?.toFixed(1)} MB), skipping: ${file?.name}`);
        return null;
      }

      try {
        const compressed = await compressImage(file);
        return { compressed, caption: photoItem?.caption };
      } catch (err) {
        console.warn('[uploadPhotos] Compression failed, using original:', err);
        return { compressed: file, caption: photoItem?.caption };
      }
    })
  );

  // Upload one compressed file and return a row object (or null on failure)
  const uploadOne = async (item) => {
    if (!item) return null;
    const { compressed, caption } = item;
    const baseName = compressed?.name
      ? sanitizeFilename(compressed?.name)?.replace(/\.[^.]+$/, '')
      : `photo_${Date.now()}_${Math.random()?.toString(36)?.slice(2)}`;
    const filePath = `${projectId}/${logId}/${baseName}.jpg`;

    const { error: uploadError } = await supabase?.storage
      ?.from(BUCKET)
      ?.upload(filePath, compressed, { upsert: true, contentType: 'image/jpeg' });

    if (uploadError) {
      console.error('Photo upload error:', uploadError);
      return null;
    }

    return {
      daily_log_id: logId,
      storage_path: filePath,
      caption: caption || null,
      uploaded_by: userId || null,
    };
  };

  // Concurrency-4 batch uploader — avoids rate limits while being 4× faster
  const BATCH_SIZE = 4;
  const validItems = compressedItems?.filter(Boolean);
  for (let i = 0; i < validItems?.length; i += BATCH_SIZE) {
    const batch = validItems?.slice(i, i + BATCH_SIZE);
    const batchRows = await Promise.all(batch?.map(uploadOne));
    for (const row of batchRows) {
      if (row) photoRows?.push(row);
    }
  }

  // Single batch insert for all successfully uploaded photos
  if (photoRows?.length > 0) {
    const { error } = await supabase?.from('daily_log_photos')?.insert(photoRows);
    if (error) console.error('Insert photo rows error:', error);
  }
};

/**
 * Met a jour la legende de photos DEJA enregistrees.
 *
 * N'ecrit QUE la colonne caption, ligne par ligne, sur l'id. Pas d'upsert :
 * daily_log_photos porte daily_log_id et storage_path en NOT NULL, qu'un
 * upsert partiel effacerait.
 *
 * `changes` = [{ id, caption }]. L'appelant n'envoie que ce qui a change ;
 * une legende videe arrive ici en chaine vide et repart en null — c'est un
 * effacement voulu, pas un oubli. Meme normalisation que l'insertion
 * (uploadPhotos), pour que les deux chemins stockent la meme chose.
 */
export const updatePhotoCaptions = async (changes = []) => {
  if (!changes?.length) return { error: null };
  try {
    // PostgrestBuilder est un PromiseLike : Promise.all l'accepte, il ne se
    // sert que de then. On n'appelle jamais .catch() dessus — il n'en a pas.
    const results = await Promise.all(
      changes?.map(({ id, caption }) =>
        supabase
          ?.from('daily_log_photos')
          ?.update({ caption: caption || null })
          ?.eq('id', id)
      )
    );
    const failed = results?.find((r) => r?.error);
    if (failed?.error) throw failed?.error;
    return { error: null };
  } catch (error) {
    console.error('updatePhotoCaptions error:', error);
    return { error };
  }
};

/**
 * Delete a single photo from storage and the database.
 */
export const deletePhoto = async (photoId) => {
  try {
    // Get the storage path first
    const { data: photo, error: fetchError } = await supabase
      ?.from('daily_log_photos')
      ?.select('storage_path')
      ?.eq('id', photoId)
      ?.single();

    if (fetchError) throw fetchError;

    // Delete from storage
    if (photo?.storage_path) {
      await supabase?.storage?.from(BUCKET)?.remove([photo?.storage_path]);
    }

    // Delete from DB
    const { error: deleteError } = await supabase
      ?.from('daily_log_photos')
      ?.delete()
      ?.eq('id', photoId);

    if (deleteError) throw deleteError;

    return { error: null };
  } catch (error) {
    console.error('deletePhoto error:', error);
    return { error };
  }
};

/**
 * Get the most recent daily log for a project BEFORE a given date.
 * Includes crew + tasks (with new persistence fields).
 * Returns null if no prior log exists.
 * @param {string} projectId
 * @param {string} currentLogDate - ISO date string (YYYY-MM-DD)
 */
export const getPreviousLogWithCrew = async (projectId, currentLogDate) => {
  try {
    const { data: log, error } = await supabase
      ?.from('daily_logs')
      ?.select(`
        id,
        log_date,
        crew:daily_log_crew(
          id,
          subcontractor_id,
          worker_count,
          notes,
          subcontractor:subcontractors(id, company_name, trade),
          tasks:daily_log_crew_tasks(id, nom, pourcentage, sort_order, previous_task_id, completed, completed_at)
        )
      `)
      ?.eq('project_id', projectId)
      ?.lt('log_date', currentLogDate)
      ?.order('log_date', { ascending: false })
      ?.limit(1)
      ?.maybeSingle();

    if (error) throw error;
    if (!log) return { data: null, error: null };

    // Sort tasks by sort_order
    const crew = (log?.crew || [])?.map((c) => ({
      ...c,
      tasks: (c?.tasks || [])?.slice()?.sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0)),
    }));

    return { data: { ...log, crew }, error: null };
  } catch (error) {
    console.error('getPreviousLogWithCrew error:', error);
    return { data: null, error };
  }
};
